import { useState, forwardRef, useImperativeHandle } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { SubscriptionItem, OptionItem } from "../../../types/rtk/currentSubscriptionsOptions/currentSubscriptionsOptions";
import Title from "../../atoms/Title/Title";
import Text from "../../atoms/Text/Text";
import ReviewCard from "../../molecules/ReviewCard/ReviewCard";

const REASON_PLACEHOLDER_KEY = "subscription:MANAGE_PAGE.EDIT.CANCEL_REVIEW.REASON.PLACEHOLDER";

export interface ReviewReasonConfig {
  key: string;
  translationKey: string;
  subReasons?: ReviewReasonConfig[];
}

export const SUBSCRIPTION_REVIEW_REASONS: ReviewReasonConfig[] = [
  {
    key: "WILL_REACTIVATE_LATER",
    translationKey: "subscription:MANAGE_PAGE.EDIT.CANCEL_REVIEW.REASON.WILL_REACTIVATE_LATER",
  },
  {
    key: "NO_USE",
    translationKey: "subscription:MANAGE_PAGE.EDIT.CANCEL_REVIEW.REASON.NO_USE",
  },
  {
    key: "LOST_BROKEN",
    translationKey: "subscription:MANAGE_PAGE.EDIT.CANCEL_REVIEW.REASON.LOST_BROKEN",
  },
  {
    key: "NOT_SATISFIED",
    translationKey: "subscription:MANAGE_PAGE.EDIT.CANCEL_REVIEW.REASON.NOT_SATISFIED",
    subReasons: [
      { key: "MISSING_FEATURES", translationKey: "subscription:MANAGE_PAGE.EDIT.CANCEL_REVIEW.REASON.MISSING_FEATURES" },
      { key: "MOBILE_APPLICATION", translationKey: "subscription:MANAGE_PAGE.EDIT.CANCEL_REVIEW.REASON.MOBILE_APPLICATION" },
      {
        key: "TRACKER_ISSUE",
        translationKey: "subscription:MANAGE_PAGE.EDIT.CANCEL_REVIEW.REASON.TRACKER_ISSUE",
        subReasons: [
          { key: "TRACKER_FORMAT", translationKey: "subscription:MANAGE_PAGE.EDIT.CANCEL_REVIEW.REASON.TRACKER_FORMAT" },
          { key: "BATTERY", translationKey: "subscription:MANAGE_PAGE.EDIT.CANCEL_REVIEW.REASON.BATTERY" },
          { key: "BAD_PRECISION", translationKey: "subscription:MANAGE_PAGE.EDIT.CANCEL_REVIEW.REASON.BAD_PRECISION" },
          { key: "BAD_NETWORK", translationKey: "subscription:MANAGE_PAGE.EDIT.CANCEL_REVIEW.REASON.BAD_NETWORK" },
          { key: "OTHER", translationKey: "subscription:MANAGE_PAGE.EDIT.CANCEL_REVIEW.REASON.OTHER" },
        ],
      },
      { key: "TOO_EXPENSIVE", translationKey: "subscription:MANAGE_PAGE.EDIT.CANCEL_REVIEW.REASON.TOO_EXPENSIVE" },
      { key: "AFTER_SALES_SERVICE", translationKey: "subscription:MANAGE_PAGE.EDIT.CANCEL_REVIEW.REASON.AFTER_SALES_SERVICE" },
      { key: "OTHER", translationKey: "subscription:MANAGE_PAGE.EDIT.CANCEL_REVIEW.REASON.OTHER" },
    ],
  },
  {
    key: "OTHER",
    translationKey: "subscription:MANAGE_PAGE.EDIT.CANCEL_REVIEW.REASON.OTHER",
  },
];

export const OPTION_REVIEW_REASONS: ReviewReasonConfig[] = [
  {
    key: "NOT_ENOUGH_FEATURES_PREMIUM",
    translationKey: "subscription:MANAGE_PAGE.EDIT.CANCEL_REVIEW.REASON.NOT_ENOUGH_FEATURES_PREMIUM",
  },
  {
    key: "TOO_EXPENSIVE",
    translationKey: "subscription:MANAGE_PAGE.EDIT.CANCEL_REVIEW.REASON.TOO_EXPENSIVE_OPTION",
  },
  {
    key: "LOST_BROKEN",
    translationKey: "subscription:MANAGE_PAGE.EDIT.CANCEL_REVIEW.REASON.LOST_BROKEN",
  },
  {
    key: "NO_USE",
    translationKey: "subscription:MANAGE_PAGE.EDIT.CANCEL_REVIEW.REASON.NO_USE",
  },
  {
    key: "NOT_SATISFIED",
    translationKey: "subscription:MANAGE_PAGE.EDIT.CANCEL_REVIEW.REASON.NOT_SATISFIED",
  },
  {
    key: "OTHER",
    translationKey: "subscription:MANAGE_PAGE.EDIT.CANCEL_REVIEW.REASON.OTHER",
  },
];

export interface ReviewCardListHandle {
  exitSubLevel: () => void;
  drillSelectedReason: () => boolean;
}

interface Props {
  item: SubscriptionItem | OptionItem | null;
  onSelectionChange?: (reasonKey: string, textValue?: string) => void;
  defaultSelected?: string;
  onDrillStateChange?: (isDrilledIn: boolean) => void;
}

const ReviewCardList = forwardRef<ReviewCardListHandle, Props>(
  ({ item, onSelectionChange, defaultSelected, onDrillStateChange }, ref) => {
    const { t } = useTranslation();
    const [selectedReason, setSelectedReason] = useState<string | null>(defaultSelected || null);
    const [reasonTexts, setReasonTexts] = useState<Record<string, string>>({});
    const [breadcrumb, setBreadcrumb] = useState<ReviewReasonConfig[]>([]);

    const topLevelReasons = item?.type === "subscription" ? SUBSCRIPTION_REVIEW_REASONS : OPTION_REVIEW_REASONS;
    const currentReasons = breadcrumb.length > 0 ? breadcrumb[breadcrumb.length - 1].subReasons! : topLevelReasons;
    const placeholder = t(REASON_PLACEHOLDER_KEY);

    useImperativeHandle(ref, () => ({
      exitSubLevel: () => {
        const newBreadcrumb = breadcrumb.slice(0, -1);
        setBreadcrumb(newBreadcrumb);
        setSelectedReason(null);
        if (newBreadcrumb.length === 0) onDrillStateChange?.(false);
      },
      drillSelectedReason: () => {
        if (!selectedReason) return false;
        const reason = currentReasons.find(r => r.key === selectedReason);
        if (reason?.subReasons?.length) {
          setBreadcrumb(prev => [...prev, reason]);
          setSelectedReason(null);
          onDrillStateChange?.(true);
          return true;
        }
        return false;
      },
    }));

    const handleSelect = (reason: ReviewReasonConfig) => {
      setSelectedReason(reason.key);
      const textValue = !reason.subReasons?.length ? reasonTexts[reason.key] || "" : undefined;
      onSelectionChange?.(reason.key, textValue);
    };

    const handleTextChange = (reasonKey: string, value: string) => {
      setReasonTexts(prev => ({ ...prev, [reasonKey]: value }));
      onSelectionChange?.(reasonKey, value);
    };

    const currentParent = breadcrumb[breadcrumb.length - 1];

    const title = currentParent
      ? t(currentParent.translationKey)
      : item?.type === "subscription"
        ? t("subscription:MANAGE_PAGE.EDIT.CANCEL_REVIEW.SUBSCRIPTION.TITLE")
        : t("subscription:MANAGE_PAGE.EDIT.CANCEL_REVIEW.OPTION.TITLE");

    const subtitle = currentParent
      ? t("subscription:MANAGE_PAGE.EDIT.CANCEL_REVIEW.DETAILS.SUBTITLE")
      : item?.type === "subscription"
        ? t("subscription:MANAGE_PAGE.EDIT.CANCEL_REVIEW.SUBSCRIPTION.SUBTITLE")
        : item?.code === "warranty"
          ? t("subscription:MANAGE_PAGE.EDIT.CANCEL_REVIEW.OPTION.SUBTITLE.WARRANTY")
          : item?.code === "premium_pack"
            ? t("subscription:MANAGE_PAGE.EDIT.CANCEL_REVIEW.OPTION.SUBTITLE.PREMIUM_PACK")
            : null;

    return (
      <Box className="ReviewCardList">
        <Box className="reviewCardListSubtitleContainer">
          <Title variant="h1" className="reviewCardListTitle" children={title} />
          <Text className="reviewCardListTextSubtitle" children={subtitle} />
        </Box>

        {currentReasons.map((reason, index) => {
          const isLeaf = !reason.subReasons?.length;
          return (
            <ReviewCard
              key={reason.key}
              index={index}
              isActive={selectedReason === reason.key}
              onSelect={() => handleSelect(reason)}
              translationKey={reason.translationKey}
              textFieldPlaceholder={isLeaf ? placeholder : undefined}
              textValue={isLeaf ? reasonTexts[reason.key] || "" : undefined}
              onTextChange={isLeaf ? (value) => handleTextChange(reason.key, value) : undefined}
            />
          );
        })}
      </Box>
    );
  }
);

ReviewCardList.displayName = "ReviewCardList";

export default ReviewCardList;
